import { useQuery } from "@tanstack/react-query";
import type { TInstitute } from "../apis/institute/institute.interfaces";
import { useInstituteService } from "../apis/institute/useInstituteService";
import type {
  TDashboardData,
  TConfigurationSummary,
} from "../apis/dashboard/dashboard.interfaces";
import { recentActivityListMock } from "../__mocks__/dashboard/recentActivityMock";

/** Page size for aggregating all institutes on the dashboard (pagination loop). */
const DASHBOARD_LIST_PAGE_SIZE = 100;

const collectAllInstitutes = async (
  getInstitutes: (params?: {
    page?: number;
    limit?: number;
  }) => Promise<TInstitute[]>,
): Promise<TInstitute[]> => {
  const all: TInstitute[] = [];
  let page = 1;
  while (true) {
    const chunk = await getInstitutes({
      page,
      limit: DASHBOARD_LIST_PAGE_SIZE,
    });
    all.push(...chunk);
    if (chunk.length < DASHBOARD_LIST_PAGE_SIZE) break;
    page += 1;
  }
  return all;
};

const buildConfigurationSummary = (
  institutes: TInstitute[],
): TConfigurationSummary => {
  if (institutes.length === 0) {
    return {
      totalInstitutes: 0,
      activeInstitutes: 0,
      averageCompleteness: 0,
    };
  }

  const activeInstitutes = institutes.filter(
    (i) => i.status === "active",
  ).length;
  const averageCompleteness =
    institutes.reduce((acc, i) => acc + i.configurationCompleteness, 0) /
    institutes.length;

  return {
    totalInstitutes: institutes.length,
    activeInstitutes,
    averageCompleteness,
  };
};

export const useDashboardHook = () => {
  const { getInstitutes } = useInstituteService();

  return useQuery<TDashboardData>({
    queryKey: ["dashboard"],
    queryFn: async () => {
      const institutes = await collectAllInstitutes(getInstitutes);
      return {
        institutes,
        configurationSummary: buildConfigurationSummary(institutes),
        // Placeholder until a dedicated dashboard / audit activity API exists.
        recentActivity: recentActivityListMock,
      };
    },
  });
};
